import './SuperUserOrdersTable.css';

import * as React from 'react';

import { IDisclaimer, IDivider, IDocument, IOrder, IOrderStatus, IUserDownloadList } from '../../../infrastructure/interfaces';
import { getScrollbarWidth } from '../../../lib/helpers';
import Select from '../../Select/Select';
import formatDate from '../../utils/formatDate';
import DocumentService from '../../../services/DocumentService';
import WarningModal from '../../Modals/WarningModal/WarningModal';
import { renderPagesCountError, renderDividersError } from '../../Warning/DocumentWarning';
import {TooltipCont} from "../../../infrastructure/tooltips";
import currencyFormatter from 'src/components/utils/formatCurrency';
import ReactPaginate from 'react-paginate';

export interface ISuperUserOrdersTableProps {
  adminOrderTypeCount: number;
  orders: IOrder[];
  statuses: IOrderStatus[];
  editDocument(doc: IDocument): void;
  onDownloadPdf(doc: IDocument, dividers: IDivider[], disclaimers: IDisclaimer[]): void;
  viewDocument(doc: IDocument, order: IOrder): void;
  changeStatus(order: any, statusId: number): void;
  onDeleteOrder(order: IOrder): void;
  getOrderTypeCount(status_id: number): void;
  getAdminOrdersList(startIndex: Number, listSize: Number, orderCategory: Number): void;
  userDownloadList: IUserDownloadList[];
  ordersPerPage: number;
  initialOrderCategoryID: number;
  dividers: IDivider[];
  disclaimers: IDisclaimer[];
}

export interface ISuperUserOrdersTableState {
  option: any;
  showPagesQuantityWarning: boolean;
  pagesError: boolean;
  dividersError: string[];
  document: IDocument | null;
  pageIndex: number;
  jumpToPageNumber: number;
}

export default class SuperUserOrdersTable extends React.Component<ISuperUserOrdersTableProps, ISuperUserOrdersTableState> {
  documentElem: HTMLElement;
  constructor(props: ISuperUserOrdersTableProps) {
    super(props);
    this.state = {
      option: { value: this.props.initialOrderCategoryID, title: '' },
      showPagesQuantityWarning: false,
      pagesError: false,
      dividersError: [],
      document: null,
      pageIndex: 0,
      jumpToPageNumber: 0,
    };
    this.handleSelectFilter = this.handleSelectFilter.bind(this);
    this.handlePageClick = this.handlePageClick.bind(this);
  }

  componentDidMount() {
    this.props.getOrderTypeCount(this.state.option.value);
  }

  onClose = () => {
    this.setState({
      showPagesQuantityWarning: false,
      pagesError: false,
      dividersError: [],
    });
  }

  onDownloadPdf(document: IDocument) {
    const validationResult = DocumentService.validateDocument(document);
    if (validationResult.errors === true) {
      const { pagesError, dividersError } = validationResult;
      this.setState({
        showPagesQuantityWarning: true,
        pagesError,
        dividersError,
        document,
      });
      return;
    }
    this.props.onDownloadPdf(document, this.props.dividers, this.props.disclaimers);
  }

  onEdit(document: IDocument) {
    this.props.editDocument(document);
  }

  filterOrders(option: any, orders: Array<IOrder>) {
    return ((option.value !== null) && (option.value != -1)) ? orders.filter(o => option.value === o.status_id) : orders;
  }

  handleSelectFilter(option: any) {
    this.setState({
      option,
      pageIndex: 0,
    }, () => {
      this.props.getOrderTypeCount(this.state.option.value);
    } );
    this.props.getAdminOrdersList(0, this.props.ordersPerPage, option.value);
  }

  handlePageClick = (data: any) => {
    const selectedPage: number = data.selected;
    this.selectPage(selectedPage);
  }

  onJumpToPageChange = (e: any) => {
    let newPageString = e.target.value;
    let newPageNumber = +newPageString - 1; // Minus 1 because pagination starts at 0, but UI is at 1
    if (Number.isInteger(newPageNumber)) {
      this.setState({
        jumpToPageNumber: newPageNumber,
      });
    }
  };

  selectPage(pageIndex: number) {
    this.setState( {
      pageIndex: pageIndex
    });
    this.props.getAdminOrdersList(pageIndex * this.props.ordersPerPage, this.props.ordersPerPage, this.state.option.value);
  }

  render() {
    const options = this.props.statuses
      .filter(status => status.admin_status === 1)
      .map(status => ({
        title: status.status_admin_name,
        value: status.id,
      }));

    const scrollWidth = getScrollbarWidth();
    const filterOptions = [...options];
    filterOptions.splice(0, 0, {
      title: 'Any',
      value: -1,
    });

    const orders = this.filterOrders(this.state.option, this.props.orders);
    const numberOfPages = Math.ceil(this.props.adminOrderTypeCount / this.props.ordersPerPage);
    const previousClassName = ((this.state.pageIndex == 0) ? "previous_page_disabled" : "previous_page");
    const nextClassName = (((this.state.pageIndex + 1) >= numberOfPages) ? "next_page_disabled" : "next_page");

    return (
      <React.Fragment>
        <WarningModal
          show={this.state.showPagesQuantityWarning}
          warningMessage={(
            <React.Fragment>
              {(this.state.pagesError && this.state.document) ? renderPagesCountError(this.state.document) : null}
              {this.state.dividersError && this.state.dividersError.length ? renderDividersError(this.state.dividersError) : null}
            </React.Fragment>
          )}
          onClose={this.onClose}
        />
        <div className="admin-table-container">
          <div className="admin-table-inner">
            <div className="admin-table-filter">
              <span className="admin-table-filter-text">Filter by Status</span>
              <Select
                onToggle={() => {

                }}
                options={filterOptions}
                selectedValue={this.state.option.value}
                onSelect={this.handleSelectFilter}
              />
            </div>  
            <div className="pagination_container">
              <ReactPaginate
                previousLabel={"← Previous"}
                nextLabel={"  Next →"}
                breakLabel={<span className="pagination_gap">...</span>}
                pageCount={numberOfPages}
                onPageChange={this.handlePageClick}
                pageRangeDisplayed={5}
                marginPagesDisplayed={5}
                forcePage={this.state.pageIndex}
                containerClassName={"pagination"}
                previousLinkClassName={previousClassName}
                nextLinkClassName={nextClassName}
                disabledClassName={"pagination_disabled"}
                activeClassName={"pagination_active"}
              />
              <div className="in-row-search-container">
                <div className="jump-to-page">
                  <input
                    type="text"
                    onKeyUp={this.onJumpToPageChange}
                    placeholder="Jump to Page"
                  />
                </div>
                  <button
                    className="btn primary-green small right"
                    onClick={() => this.selectPage(this.state.jumpToPageNumber)}
                    title="Jump to Page">
                      Go
                  </button>
              </div>
            </div>
            <table className="table-scrollable admin-documents-table">
              <thead style={{ paddingRight: scrollWidth }}>
                <tr>
                  <th>Order ID</th>
                  <th>Date</th>
                  <th>User Name</th>
                  <th>Order Name</th>
                  <th>Type</th>
                  <th>Edited Date</th>
                  <th>Quantity</th>
                  <th>Shipping Addresses</th>
                  <th>Download</th>
                  <th>Actions</th>
                  <th>Status</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody className="admin-table-body">
                {orders.map((o, index, array) =>
                  <tr key={o.id} ref={tr => this.documentElem = tr!}>
                    <td>{o.id}</td>
                    <td>{formatDate(o.created_at)}</td>
                    <td>{(o.user != null) ? o.user.username : ""}</td>
                    <td>{o.document.name}</td>
                    <td>{o.document.type}</td>
                    <td>{formatDate(o.last_modified)}</td>
                    <td>{o.orderQuantitiesAddresses.map(o => o.orderQuantity).reduce((acc, x) => (acc + x), 0)}</td>
                    <td>
                      <a href="#" role="button" onClick={() => this.props.viewDocument(o.document, o)}>
                        View
                    </a>
                    </td>
                    <td>
                      <button
                        className="btn download"
                        onClick={() => this.onDownloadPdf(o.document)}
                        disabled={this.props.userDownloadList.some(el => el.id === o.document.id)}
                      >
                        <img className="download-logo" src={require('./img/downloadsNew.svg')} />
                        <span>PDF</span>
                      </button>
                    </td>
                    <td>
                        <div>
                            <button
                                className="btn primary-blue"
                                onClick={() => this.onEdit(o.document)}
                                title={TooltipCont.EDIT}
                            >
                                Edit
                            </button>
                        </div>
                        <div>
                            <button
                                style={{marginTop: 5}}
                                className="btn primary-red"
                                onClick={() => this.props.onDeleteOrder(o)}
                                title={TooltipCont.DELETE}
                                disabled={false}
                            >
                                Delete
                            </button>
                        </div>
                    </td>
                    <td>
                      <Select
                        onToggle={() => {
                          const isLast = array.length - 1 === index ? true : false;
                          if (isLast) {
                            const adminTable = this.documentElem.closest('.admin-table-body');
                            if (adminTable != null) {
                              setTimeout(() => {
                                adminTable.scrollTop = adminTable.scrollHeight;
                              }, 0);
                            }
                          }
                        }}
                        options={options}
                        selectedValue={o.status_id}
                        onSelect={option => this.props.changeStatus(o, option.value)}
                      />
                    </td>
                    <td>{o.cost ? currencyFormatter.format(o.cost) : "--"}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </React.Fragment>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/SuperUser/components/SuperUserOrdersTable.tsx